import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SectionHeading from 'components/SectionHeading';
import SectionSubtitle from 'components/SectionSubtitle';
import ProductDetailedButton from '../../../Product/components/DetailedButton/DetailedButton';
import { groupByCategory } from '../../../Product/utils';
import { selectProductItems } from '../../../Product/store/selectors';
import { sendEvent } from '../../../utils/google-analytics';
import { getRoute } from '../../../utils/pages';
import { ProductCategory } from '../../../types';
import i18n from 'i18n';
import styles from './CategoryCards.module.css';
import circle from './Circle 4.svg';

const useStyles = makeStyles((theme) => ({
  list: {
    listStyle: 'none',
    padding: theme.spacing(0, 0.55),
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    maxWidth: 900,
    alignItems: 'stretch',
    boxSizing: 'border-box',
    [theme.breakpoints.down('lg')]: {
      maxWidth: 640,
      gap: theme.spacing(0.75),
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      maxWidth: 480,
      gap: theme.spacing(0.75),
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      maxWidth: 320,
      gap: theme.spacing(0.75),
    },
  },
  listItem: {
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0,
    '& button': {
      width: '100%',
      maxWidth: 180,
      height: '100%',
      overflow: 'hidden',
      [theme.breakpoints.up('sm')]: {
        maxWidth: 200,
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: 220,
      },
      [theme.breakpoints.up('lg')]: {
        maxWidth: 240,
      },
      [theme.breakpoints.up('xl')]: {
        maxWidth: 260,
      },
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: theme.spacing(0.55),
      background: '#f9fafb',
      border: '1px solid rgba(51, 51, 51, 0.25)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      padding: theme.spacing(1.9),
      [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(0.85),
      },
      [theme.breakpoints.up('md')]: {
        gap: theme.spacing(1.1),
      },
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        margin: 0,
        width: '100%',
      },
      '& > div:last-of-type': {
        marginLeft: 0,
        marginRight: 0,
        paddingRight: 0,
      },
      '& .image': {
        padding: 0,
      },
      '& .MuiTypography-h5': {
        marginBottom: 0,
        display: 'block',
        width: '100%',
        textAlign: 'center',
        fontFamily: theme.typography.fontFamily,
        textTransform: 'uppercase',
        fontWeight: 800,
        fontSize: '0.875rem',
        letterSpacing: '0.06em',
        [theme.breakpoints.up('sm')]: {
          fontSize: '1rem',
        },
        [theme.breakpoints.up('md')]: {
          fontSize: '1.0625rem',
        },
        [theme.breakpoints.up('lg')]: {
          fontSize: '1.125rem',
        },
      },
      '& img, & svg': {
        width: '8.5rem',
        height: '8.5rem',
        flexShrink: 0,
        objectFit: 'contain',
        [theme.breakpoints.up('sm')]: {
          width: '8.5rem',
          height: '8.5rem',
        },
        [theme.breakpoints.up('md')]: {
          width: '9.2rem',
          height: '9.2rem',
        },
        [theme.breakpoints.up('lg')]: {
          width: '10rem',
          height: '10rem',
        },
        [theme.breakpoints.up('xl')]: {
          width: '10.8rem',
          height: '10.8rem',
        },
      },
      '& svg': {
        fill: `${theme.palette.secondary.main} !important`,
      },
      '& svg *': {
        fill: `${theme.palette.secondary.main} !important`,
      },
    },
  },
}));

export default function CategoryCards() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const products = useSelector(selectProductItems);
  const categories = products ? groupByCategory(products) : [];

  if (!categories || categories.length === 0) {
    return null;
  }

  const onCategoryClick = (x: ProductCategory) => {
    const slug = x.seo?.[i18n.language] ?? x.seo?.en ?? x.seo?.de;
    if (slug) {
      sendEvent('repair', `category_selected-name_${x.title.de}`, x.title.de);
      navigate(`/${getRoute('product')}/${slug}`);
    }
  };

  return (
    <section className={styles.section} aria-labelledby="category-cards-heading">
      <div className={styles.container}>
        <header className={styles.header}>
          <SectionHeading id="category-cards-heading" size="large" className={styles.title}>
            {t('CategoryCards title prefix')}
            <span className={styles.circleWrap}>
              <span>{t('CategoryCards title highlight')}</span>
              <img src={circle} alt="" aria-hidden className={styles.circle} />
            </span>
          </SectionHeading>
          <SectionSubtitle size="medium" className={styles.subtitle}>{t('CategoryCards subtitle')}</SectionSubtitle>
        </header>

        <ul aria-labelledby="category-cards-heading" className={classes.list}>
          {categories.map((x, i) => (
            <Box component="li" key={x._id} className={classes.listItem}>
              <ProductDetailedButton
                onClick={() => onCategoryClick(x)}
                name={x.title}
                imageUrl={x.imageUrl}
              />
            </Box>
          ))}
        </ul>
      </div>
    </section>
  );
}
